import React, { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom-v5-compat";
import { z } from "zod";

import { Label } from "@onsite_app/MobileOrderingMenu/Atoms/Form";

import {
  Button,
  Price,
  PrimaryAction,
  useSubmitButton,
  LinkButton,
  PriceLine,
} from "@online_ordering/Atoms";

import {
  CartDetailsForm,
  PickUpOptions,
} from "@online_ordering/Pages/Cart/CartDetailsForm";
import { CartItems } from "@online_ordering/Pages/Cart/CartItems";
import { OrderedOrderItems } from "@online_ordering/Pages/Cart/OrderedOrderItem";
import { SoldOutItemsError } from "@online_ordering/Pages/Cart/errors";
import { CartQueries } from "@online_ordering/Pages/Cart/queries";
import { createCartCheckoutSession } from "@online_ordering/Pages/Cart/requests";
import { useCartTotals } from "@online_ordering/Pages/Cart/useCartTotal";
import OnlineOrderingDisabled, {
  useOnlineOrderingAvailableNow,
} from "@online_ordering/Pages/OnlineOrderingSchedule/OnlineOrderingDisabled";
import { useCurrentCartUuid } from "@online_ordering/Pages/Session/SessionContext";

import { useVenue } from "@online_ordering/VenueContext";
import { useFetchLiveItemCountdowns } from "@online_ordering/queries";
import {
  getVenueHasSimpleOrdering,
  getVenueHasTableService,
} from "@online_ordering/venueSelectors";

import { useNextCartMutation, useUpdateCart } from "../Cart/mutations";
import VenueOnlineConfigQueries from "../VenueOnlineConfig/queries";
import VoucherAssignmentsList from "./VoucherAssignments/VoucherAssignmentsList";

const FormDataSchema = z
  .object({
    name: z.string().trim().min(4),
    floorTableId: z
      .string()
      .nullish()
      .transform((v) => (!v ? null : v)),
    pickUpInMinutes: z
      .union([z.string(), z.number(), z.null()])
      .optional()
      .transform((v) => (!v ? null : Number(v)))
      .nullish(),
    pickUpMethod: z.enum(["takeaway", "have_here"]),
  })
  .superRefine((data, ctx) => {
    if (data.pickUpMethod === "have_here" && !data.floorTableId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Table number is required for have_here orders",
        path: ["floorTableId"],
      });
    }
  });

export type SimpleOrderFormData = z.infer<typeof FormDataSchema>;

const RedeemVoucherLink = ({ children }: { children: React.ReactNode }) => {
  const vouchersEnabled = useSuspenseQuery({
    ...VenueOnlineConfigQueries.current(),
    select: (data) => data.hasActiveVouchers,
  }).data;
  if (!vouchersEnabled) {
    return null;
  }
  return (
    <Label>
      <Link
        className="font-normal underline underline-offset-2"
        to="./lookup-voucher"
      >
        {children}
      </Link>
    </Label>
  );
};
export const SimpleOrderCart = () => {
  const { t } = useTranslation("onsite");
  const venue = useVenue();
  const tableServiceEnabled = getVenueHasTableService(venue);
  const orderingEnabled = getVenueHasSimpleOrdering(venue);
  const [submissionState, trackSubmit] = useSubmitButton();
  const cartUuid = useCurrentCartUuid();
  const cart = useSuspenseQuery(CartQueries.cart(cartUuid)).data;
  const totals = useCartTotals(cartUuid);
  const [soldOutItemsUuids, setSoldOutItemsUuids] = useState<string[]>([]);
  const initialPickupMethod =
    orderingEnabled && tableServiceEnabled ? "eatIn" : "takeaway";

  const itemCountdownQuery = useFetchLiveItemCountdowns();
  const updateCartMutation = useUpdateCart();
  const soldOutItems = itemCountdownQuery.data?.filter(
    (i) => i.remainingCount <= 0,
  );
  const hasSoldOutItems =
    cart.items
      .map((i) => i.itemId)
      .filter(
        (uuid) =>
          (soldOutItems || []).map((t) => t.itemUuid).includes(uuid) ||
          soldOutItemsUuids.includes(uuid),
      ).length > 0;

  const { register, handleSubmit, getValues, ...formProps } =
    useForm<SimpleOrderFormData>({
      resolver: zodResolver(FormDataSchema),
      defaultValues: {
        name: cart.name || "",
        floorTableId: cart.floorTableId || undefined,
        pickUpInMinutes: cart.pickUpInMinutes || undefined,
        pickUpMethod: cart.pickUpMethod || initialPickupMethod,
      },
    });
  const { errors } = formProps.formState;

  const navigate = useNavigate();
  const onCartDetailsChanged = async (data: SimpleOrderFormData) => {
    updateCartMutation.mutateAsync({
      uuid: cart.uuid,
      venueId: cart.venueId,
      name: data.name,
      floorTableId: data.floorTableId || undefined,
      pickUpInMinutes: data.pickUpInMinutes,
      pickUpMethod: data.pickUpMethod,
    });
  };
  const nextCartMutation = useNextCartMutation();
  const checkoutCart = async (data: SimpleOrderFormData) => {
    try {
      await onCartDetailsChanged(data);

      if (!orderingEnabled) {
        console.error("Ordering is not enabled");
        return;
      }
      const response = await createCartCheckoutSession(cart.uuid);
      if (response.nextAction.type === "order_success") {
        // Order is free or paid in full with vouchers
        await nextCartMutation.mutateAsync();
        navigate(`/cart/checkout/${response.checkoutSession.uuid}/success`);
        await new Promise((r) => setTimeout(r, 3000));
      } else if (response.nextAction.type === "payment_redirect") {
        const checkoutUrl = response.nextAction.redirectUrl;
        window.location.assign(checkoutUrl);
        await new Promise((r) => setTimeout(r, 3000));
      }
    } catch (e) {
      if (e instanceof SoldOutItemsError) {
        console.log(e);
        setSoldOutItemsUuids(e.soldOutItemsUuids);
        return;
      }
      console.error(e);
    }
  };

  const onlineOrderingAvailable = useOnlineOrderingAvailableNow();
  const checkoutButton = !hasSoldOutItems ? (
    <Button
      disabled={!onlineOrderingAvailable || cart.items.length == 0}
      submissionState={submissionState}
      primary
    >
      {t("cart.payNow")}{" "}
      <Price showDecimals showCurrency value={totals.totalDue} />
    </Button>
  ) : (
    <div className="bg-venue-secondary text-center text-venue-highlight">
      {t("cart.orderingSoldOutItems")}{" "}
    </div>
  );
  const recordChanges = () => {
    trackSubmit(handleSubmit(onCartDetailsChanged))(getValues());
  };
  return (
    <>
      <form onSubmit={handleSubmit(trackSubmit(checkoutCart))}>
        <div className="space-y-32">
          <div className="flex-1 space-y-32 pb-12">
            <OnlineOrderingDisabled />
            {orderingEnabled && tableServiceEnabled && (
              <PickUpOptions
                register={register}
                recordChange={recordChanges}
                isEditable
              />
            )}
            <CartItems soldOutItemsUuids={soldOutItemsUuids} />
            <div className="">
              <LinkButton secondary to="/">
                {t("cart.addMoreItems")}
              </LinkButton>
            </div>
            <CartDetailsForm
              register={register}
              isHaveHere={formProps.watch("pickUpMethod") === "have_here"}
              onChange={recordChanges}
              errors={errors}
              isEditable
            />

            {totals.totalVoucherValue > 0 ? (
              <div className="space-y-12">
                <>
                  <PriceLine
                    label={t("cart.subtotal")}
                    value={
                      <Price
                        showCurrency
                        showDecimals
                        value={totals.subTotal}
                      />
                    }
                  />
                  <PriceLine
                    label={t("cart.voucherTotalApplied")}
                    value={
                      <Price
                        showCurrency
                        showDecimals
                        value={-totals.voucherTotalApplied}
                      />
                    }
                  />
                  <div className="pb-8">
                    <VoucherAssignmentsList />
                  </div>
                  {totals.totalDue > 0 && (
                    <RedeemVoucherLink>
                      {t("cart.redeemAnotherVoucher")}
                    </RedeemVoucherLink>
                  )}
                </>
                <PriceLine
                  label={t("cart.totalToPay")}
                  value={
                    <Price showCurrency showDecimals value={totals.totalDue} />
                  }
                />
              </div>
            ) : (
              <>
                <div className="space-y-8">
                  <PriceLine
                    label={t("cart.totalToPay")}
                    value={
                      <Price
                        showCurrency
                        showDecimals
                        value={totals.totalDue}
                      />
                    }
                  />
                  <RedeemVoucherLink>
                    {t("cart.redeemVoucher")}
                  </RedeemVoucherLink>
                </div>
              </>
            )}
            <div className="hidden items-center space-x-32 md:flex">
              {orderingEnabled && (
                <div className="hidden flex-1 md:block">{checkoutButton}</div>
              )}
            </div>
            <OrderedOrderItems />
          </div>
        </div>
        {orderingEnabled && <PrimaryAction>{checkoutButton}</PrimaryAction>}
      </form>
    </>
  );
};
